import * as React from 'react'

import {
  Blend,
  Building2,
  CalendarClock,
  CalendarOff,
  Clock,
  MapPin,
  MoreHorizontal,
  ToggleRight,
  Trash2,
} from 'lucide-react'
import { observer } from 'mobx-react'

import { ILocationClosureInterval, RestrictionType } from '~/client/graph'
import {
  CompanyGroupingOption,
  CompanyListSelector,
  ICompanyMetadata,
} from '~/client/src/shared/components/CompanyListSelector'
import ConfirmDialog from '~/client/src/shared/components/ConfirmDialog/ConfirmDialog'
import { DisabledReasonTooltip } from '~/client/src/shared/components/DisabledReasonTooltip'
import DynamicOverflowList from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList'
import DynamicOverflowListStore from '~/client/src/shared/components/DynamicOverflowList/DynamicOverflowList.store'
import IntervalEditor, {
  NextDayLabel,
} from '~/client/src/shared/components/IntervalEditor'
import { InheritedHoursChip } from '~/client/src/shared/components/ResourceControls/cells/InheritedProjectChip'
import { normalizeAllToEmptyIds } from '~/client/src/shared/components/ResourceControls/controlKeys'
import { formatRestrictionDraft } from '~/client/src/shared/components/ResourceControls/formatWorkflowRestriction'
import {
  buildDefaultClosure,
  buildInheritedHoursRows,
  buildOperatingIntervalSeed,
  buildWeekdayScheduleRows,
} from '~/client/src/shared/components/ResourceControls/intervalDefaults'
import { IAllowanceEdit } from '~/client/src/shared/components/ResourceControls/resourceFieldAllowanceWrites'
import WorkflowTypeFieldSelector from '~/client/src/shared/components/ResourceControls/WorkflowTypeFieldSelector'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/client/src/shared/components/shadcn-ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
} from '~/client/src/shared/components/shadcn-ui/popover'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import { Tag } from '~/client/src/shared/components/Tag/Tag'
import {
  DUPLICATE_WORKFLOW_SET_ERROR,
  toWorkflowChips,
  WorkflowRestrictionEditor,
} from '~/client/src/shared/components/WorkflowRestrictionEditor'
import { WorkflowTypeListSelector } from '~/client/src/shared/components/WorkflowTypeListSelector'
import { WorkflowIcon } from '~/client/src/shared/icons'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import type { IWorkflowRestrictionDraft } from '~/client/src/shared/resources/domain/resourceControls'
import ProjectDateStore, {
  MINUTES_IN_HOUR,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import IMsDateInterval from '~/client/src/shared/types/IMsDateInterval'
import {
  formatClosureIntervalFmt,
  formatOperatingIntervalFmt,
} from '~/client/src/shared/utils/intervalFormatters'
import { NOOP } from '~/client/src/shared/utils/noop'
import {
  isWeekdayScopedInterval,
  resolveOperatingIntervalIsDaily,
} from '~/client/src/shared/utils/operatingIntervalMode'
import {
  findDailyOperatingInterval,
  upsertOperatingInterval,
} from '~/client/src/shared/utils/operatingIntervals'
import { toast } from '~/client/src/shared/utils/toast'
import { areArraysEqualUnordered } from '~/client/src/shared/utils/util'

import BulkListEditor, { IBulkListEditorRenderArgs } from './BulkListEditor'
import {
  DATE_CLOSURES_KEY,
  HOURS_OF_OPERATION_KEY,
  MAX_BOOKING_DURATION_KEY,
  MAX_OVERLAPPING_KEY,
  PERMITTED_COMPANIES_KEY,
  SHOW_IN_MAP_KEY,
  WORKFLOW_TYPES_KEY,
} from './resourceControlsColumns'
import { IResourceControlsLabels } from './types'

// Stable empty selection for the bulk Permitted Companies picker — a fresh
// inline Set would change identity on every render and reset the selector's
// in-progress draft (useListSelectorState re-seeds the draft on selectedIds
// identity change).
const EMPTY_SELECTION = new Set<string>()

// Keep the overflow column actions in the same blue as the inline bulk buttons,
// including on hover — overriding DropdownMenuItem's default accent focus styles
// (the variant chains mirror the primitive's so tailwind-merge wins). Delete
// keeps the built-in destructive variant.
const MORE_ITEM_PRIMARY =
  'tw:text-primary tw:focus:bg-primary/10 tw:focus:text-primary tw:not-data-[variant=destructive]:focus:**:text-primary'

// Bulk-action descriptor keys. Two of them differ from the CONTROL key they edit -
// 'maxBookingDuration'/'maxOverlapping' against controls 'maxBookingDurations'/
// 'maxOverlappingBookings' - so denial has to be expressed in action keys, not control keys.
const PERMITTED_COMPANIES_ACTION_KEY = 'permittedCompanies'
const MAX_BOOKING_DURATION_ACTION_KEY = 'maxBookingDuration'
const MAX_OVERLAPPING_ACTION_KEY = 'maxOverlapping'
const DELETE_ACTION_KEY = 'delete'

// What the project's site root forbids in bulk. Delete is in here and deliberately NOT in
// PROJECT_LOCATION_DENIED_CONTROL_KEYS: that array is the control vocabulary behind the
// card's control list and its save gate, and Delete is not a control.
// Read inside render, never at module scope - a translator captured at module scope is
// frozen in whatever language loaded first.
const deniedReasonFor = (action: IBulkAction): string =>
  action.key === DELETE_ACTION_KEY
    ? Localization.translator.projectLocationDeletionProhibited
    : Localization.translator.projectLocationControlEditProhibited(action.label)

const PROJECT_LOCATION_DENIED_ACTION_KEYS: ReadonlySet<string> = new Set([
  PERMITTED_COMPANIES_ACTION_KEY,
  MAX_BOOKING_DURATION_ACTION_KEY,
  MAX_OVERLAPPING_ACTION_KEY,
  DELETE_ACTION_KEY,
])

// Width the overflow algorithm reserves for the More trigger, and a buffer that
// covers the per-item right margins (excluded from offsetWidth) so the row
// doesn't overflow before the More button appears.
const MORE_BUTTON_WIDTH = 40
const ITEM_GAP_BUFFER = 40

// A bulk action: either opens a column editor in a popover (renderEditor) or
// runs a direct command (onSelect, e.g. Delete -> confirm dialog).
interface IBulkAction {
  key: string
  label: string
  icon: React.ReactNode
  popoverClassName?: string
  renderEditor?: (close: () => void) => React.ReactNode
  onSelect?: () => void
  // The control this action edits (matches RESOURCE_CONTROL_KEYS); the action is
  // hidden unless that control is in the resource's `controls`. Structural
  // actions (WR toggle, Delete) leave it undefined and always show.
  controlKey?: string
  // Denied for this selection. Rendered greyed out rather than dropped, so the admin sees
  // the action exists and simply does not apply here.
  isDenied?: boolean
}

interface IProps {
  selectedCount: number
  /** The resource family whose permit-type fields the Workflow Types editor lists. */
  resourceTypeId: string
  companies: Company[]
  companyMetadata?: Map<string, ICompanyMetadata>
  getPermitTypeById: (id: string) => WorkflowType | undefined
  allWorkflowTypeIds: string[]
  projectDateStore: ProjectDateStore
  /** Receives an answer for every listed field; the selection takes exactly those. */
  onBulkWorkflowTypes: (edits: IAllowanceEdit[]) => void | Promise<void>
  onBulkPermittedCompanies: (companyIds: string[]) => void | Promise<void>
  onBulkOperatingIntervals: (
    intervals: IMsDateInterval[],
  ) => void | Promise<void>
  /** Navigates to the project settings Time tab; drives the inherited-hours chip link. */
  onEditProjectHours?: () => void
  onBulkClosures: (
    closureIntervals: ILocationClosureInterval[],
  ) => void | Promise<void>
  // Lazily produces the unique union of the selected rows' active, non-permit
  // closures (matching the Date closures column), seeded as the editor's default.
  getDefaultClosures: () => ILocationClosureInterval[]
  onBulkRestrictions: (
    restrictionType: RestrictionType,
    drafts: IWorkflowRestrictionDraft[],
  ) => void | Promise<void>
  onBulkBookable: (isBookable: boolean) => void | Promise<void>
  onBulkShowInMap: (showInMap: boolean) => void | Promise<void>
  onDelete: () => void | Promise<void>
  confirmDelete: IResourceControlsLabels['confirmDelete']
  controls: readonly string[]
  hasProjectLocationSelected: boolean
}

// The overflow menu: a blue three-dots trigger listing the actions that didn't
// fit on the row (`menuActions`). A dumb trigger — selecting a column action
// calls `onActivate(key)` to open the single bar-level editor (which survives
// reflow); Delete runs its own `onSelect`. `registerAnchor` exposes the trigger
// element so the bar-level editor can anchor to it while the action is overflowed.
function BulkMoreMenu({
  menuActions,
  onActivate,
  registerAnchor,
  isApplying,
}: {
  menuActions: IBulkAction[]
  onActivate: (key: string) => void
  registerAnchor: (el: HTMLElement | null) => void
  /** An apply is in flight; switching action now would discard the open editor's draft. */
  isApplying: boolean
}) {
  return (
    <span ref={registerAnchor} className="tw:inline-flex">
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label={Localization.translator.more}
          render={<Button variant="primary-ghost" className="tw:px-1.5" />}
        >
          <MoreHorizontal />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="tw:w-[220px]">
          {menuActions.map(action => (
            <DisabledReasonTooltip
              key={action.key}
              role="none"
              className="tw:flex"
              isDenied={!!action.isDenied}
              reason={action.isDenied ? deniedReasonFor(action) : ''}
            >
              <DropdownMenuItem
                variant={action.onSelect ? 'destructive' : 'default'}
                className={action.onSelect ? undefined : MORE_ITEM_PRIMARY}
                disabled={action.isDenied || isApplying}
                onClick={() =>
                  action.onSelect ? action.onSelect() : onActivate(action.key)
                }
              >
                {action.icon}
                {action.label}
              </DropdownMenuItem>
            </DisabledReasonTooltip>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </span>
  )
}

function BulkRestrictionEditor({
  label,
  title,
  restrictionType,
  getPermitTypeById,
  allWorkflowTypeIds,
  initialValue,
  initialWorkflowIds,
  onApply,
  onCancel,
}: {
  label: string
  title: string
  restrictionType: RestrictionType
  getPermitTypeById: (id: string) => WorkflowType | undefined
  allWorkflowTypeIds: string[]
  initialValue?: string
  initialWorkflowIds?: string[]
  onApply: (value: string, workflowIds: string[]) => void
  onCancel: () => void
}) {
  const [value, setValue] = React.useState(initialValue ?? '1')
  // "Any" is kept as a flag (persisted as []) so it always covers current +
  // future types, rather than materializing a stale snapshot of allWorkflowTypeIds.
  const [workflowIds, setWorkflowIds] = React.useState<string[]>(
    initialWorkflowIds ?? [],
  )
  const [isAny, setIsAny] = React.useState(!initialWorkflowIds?.length)
  const [isWorkflowListOpen, setIsWorkflowListOpen] = React.useState(false)

  // "Any" shows every current type checked (edit-down); a concrete subset shows
  // itself (possibly empty, so deselect-all works). Derived from the live all-set.
  const effectiveWorkflowIds = isAny ? allWorkflowTypeIds : workflowIds

  const handleSelectionChange = (ids: string[]) => {
    setWorkflowIds(ids)
    setIsAny(false)
  }

  return (
    <WorkflowRestrictionEditor
      label={label}
      restrictionType={restrictionType}
      value={value}
      onValueChange={event => setValue(event.target.value)}
      chipWorkflows={toWorkflowChips(effectiveWorkflowIds, getPermitTypeById)}
      onWorkflowRemove={id =>
        handleSelectionChange(
          effectiveWorkflowIds.filter(workflowId => workflowId !== id),
        )
      }
      isWorkflowListOpen={isWorkflowListOpen}
      onWorkflowListOpenChange={setIsWorkflowListOpen}
      workflowList={
        <WorkflowTypeListSelector
          useActualTypes
          title={Localization.translator.selectWorkflowTypes}
          onClose={() => setIsWorkflowListOpen(false)}
          selectedIds={effectiveWorkflowIds}
          onSelectionChange={handleSelectionChange}
        />
      }
      onSave={() => onApply(value, isAny ? [] : workflowIds)}
      onCancel={onCancel}
      title={title}
    />
  )
}

// Shared body for the bulk boolean-toggle editors (WR, Show in Maps): a title +
// Switch, a state-dependent description, and a Cancel/Apply footer. The checked
// state is the value applied; it seeds fresh from `defaultChecked` on each open
// (the bar-level Popover remounts the editor by active-action key).
function BulkToggleEditor({
  title,
  defaultChecked,
  describe,
  onApply,
  onCancel,
}: {
  title: string
  defaultChecked: boolean
  describe: (checked: boolean) => React.ReactNode
  onApply: (checked: boolean) => void
  onCancel: () => void
}) {
  const [checked, setChecked] = React.useState(defaultChecked)
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <span className="tw:text-title-sm tw:font-semibold tw:text-foreground">
          {title}
        </span>
        <Switch checked={checked} onCheckedChange={setChecked} />
      </div>
      <p className="tw:m-0 tw:text-body-md tw:text-muted-foreground">
        {describe(checked)}
      </p>
      <div className="tw:flex tw:items-center tw:justify-end tw:gap-2 tw:pt-1">
        <Button variant="outline" onClick={onCancel}>
          {Localization.translator.cancel}
        </Button>
        <Button onClick={() => onApply(checked)}>
          {Localization.translator.apply}
        </Button>
      </div>
    </div>
  )
}

function ResourceControlsSelectionBar({
  selectedCount,
  resourceTypeId,
  companies,
  companyMetadata,
  getPermitTypeById,
  allWorkflowTypeIds,
  projectDateStore,
  onBulkWorkflowTypes,
  onBulkPermittedCompanies,
  onBulkOperatingIntervals,
  onEditProjectHours,
  onBulkClosures,
  getDefaultClosures,
  onBulkRestrictions,
  onBulkBookable,
  onBulkShowInMap,
  onDelete,
  confirmDelete,
  controls,
  hasProjectLocationSelected,
}: IProps) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  // Guards bulk Apply against double-submission: while a write is in flight the
  // chip-list editor's Apply/Cancel are disabled. Always reset afterward — this
  // bar renders null when nothing is selected but stays mounted, so a stale
  // true would leave a later popover's buttons permanently disabled.
  const [isApplying, setIsApplying] = React.useState(false)
  // Read by closeEditor, which is memoized with no deps so it can be handed to editors as a
  // stable `close`; a ref keeps it seeing the current value without re-creating it.
  const isApplyingRef = React.useRef(false)
  isApplyingRef.current = isApplying

  // DynamicOverflowList observes its own container, so it recalculates on any
  // width change (not just window resize) — no per-consumer ResizeObserver needed.
  const overflowStore = React.useRef(new DynamicOverflowListStore()).current

  // Single source of truth for the open bulk editor. Inline buttons and the More
  // menu only set this key; the editor renders once at the bar level (below), so
  // it survives DynamicOverflowList moving its action between the row and More
  // (which would otherwise unmount a per-trigger editor and drop the draft).
  const [activeActionKey, setActiveActionKey] = React.useState<string | null>(
    null,
  )
  // The "More" trigger element — the one trigger the editor can't capture from a
  // click event, since overflow actions are picked from inside the dropdown.
  const moreTriggerRef = React.useRef<HTMLElement | null>(null)
  // Snapshot of the trigger that opened the current editor (element + frozen rect
  // + alignment). The editor anchors to the live element while it's mounted; once
  // that trigger unmounts (its action reflows across the inline/overflow
  // boundary) it falls back to the frozen rect, so the popover stays where it was
  // opened rather than jumping to the top-left. Persists past close so finalFocus
  // can return focus to the opening trigger.
  const activeAnchorRef = React.useRef<{
    el: HTMLElement
    rect: DOMRect
    align: 'start' | 'end'
  } | null>(null)
  // Caches the frozen-rect anchor object so its identity stays stable across
  // renders once the trigger has unmounted (a new object each render would make
  // base-ui re-register the position reference and recompute placement every
  // render). Reset whenever a new editor opens or the live element returns.
  const frozenAnchorRef = React.useRef<{
    getBoundingClientRect: () => DOMRect
  } | null>(null)

  const activateAction = (
    key: string,
    triggerEl: HTMLElement | null,
    align: 'start' | 'end',
  ) => {
    // Switching action unmounts the open editor, which is the same loss of a draft that
    // closeEditor refuses mid-write - and worse, it would defeat runApply keeping the
    // editor open on a failed apply, since the draft is gone before the failure lands.
    if (isApplyingRef.current) return

    activeAnchorRef.current = triggerEl
      ? { el: triggerEl, rect: triggerEl.getBoundingClientRect(), align }
      : null
    frozenAnchorRef.current = null
    setActiveActionKey(key)
  }
  // Every dismissal path routes here - the editors' own Cancel (they are handed this as
  // `close`), an outside click and Esc - so guarding it once holds the editor open for the
  // whole of an in-flight apply. Without that the editor could be dismissed mid-write, the
  // user could change the selection, and the apply's trailing clearSelection would then
  // clear whatever they had picked in the meantime.
  const closeEditor = React.useCallback(() => {
    if (isApplyingRef.current) return
    setActiveActionKey(null)
  }, [])

  // The bar stays mounted (the toolbar always renders it) and returns null with
  // no selection. Reset any open editor/delete dialog when the selection empties
  // so a later, different selection can't resurrect a stale popover or dialog.
  React.useEffect(() => {
    if (selectedCount === 0) {
      setActiveActionKey(null)
      setIsDialogOpen(false)
    }
  }, [selectedCount])

  // Same reason, narrower trigger: adding the site root to the selection denies four
  // actions, and an editor already open on one of them would otherwise stay live behind a
  // now-disabled button. Clearing the key rather than filtering the descriptor is what stops
  // it reopening by itself when the root leaves the selection again.
  React.useEffect(() => {
    if (!hasProjectLocationSelected) return

    setActiveActionKey(key =>
      key && PROJECT_LOCATION_DENIED_ACTION_KEYS.has(key) ? null : key,
    )
  }, [hasProjectLocationSelected])

  const handleDelete = React.useCallback(async () => {
    setIsDeleting(true)
    try {
      await onDelete()
      setIsDialogOpen(false)
    } catch (error) {
      console.error('Failed to delete:', error)
    } finally {
      setIsDeleting(false)
    }
  }, [onDelete])

  const runApply = React.useCallback(
    async (apply: () => void | Promise<void>, close: () => void) => {
      setIsApplying(true)
      isApplyingRef.current = true
      try {
        // Most bulk handlers surface their own error toast and don't rethrow, so apply()
        // resolves on both success and handled failure; close() then dismisses the popover
        // (a successful apply also clears the selection).
        await apply()
        // Drop the guard BEFORE closing: it exists to refuse a dismissal the user asks for
        // mid-write, not this one, which is the apply deliberately dismissing itself once
        // the write has landed. Leaving it set would make close() a no-op and strand the
        // editor open after every handled failure in the actions that toast and resolve.
        isApplyingRef.current = false
        close()
      } catch {
        // A handler that DOES rethrow is saying the write did not land. Leave the editor
        // open so the draft survives - it has already shown the user why - rather than
        // closing over a selection they would have to reconstruct from nothing.
      } finally {
        isApplyingRef.current = false
        setIsApplying(false)
      }
    },
    [],
  )

  // Shared editor body for both restriction chips (Max booking duration / Max
  // overlapping) — they differ only by type and label. Normalizes the workflow
  // set ("all types" → [] = Any) and rejects a set already staged on another
  // chip, mirroring the per-row WorkflowRestrictionCell guard.
  const renderRestrictionEditor =
    (restrictionType: RestrictionType, label: string) =>
    ({
      initial,
      items,
      editIndex,
      commit,
      close,
    }: IBulkListEditorRenderArgs<IWorkflowRestrictionDraft>) => (
      <BulkRestrictionEditor
        label={label}
        // No initial item means the "+"/empty-space add; a chip click edits it.
        title={
          initial
            ? Localization.translator.editX(label)
            : Localization.translator.createNewX(label)
        }
        restrictionType={restrictionType}
        getPermitTypeById={getPermitTypeById}
        allWorkflowTypeIds={allWorkflowTypeIds}
        initialValue={initial?.values[0]}
        initialWorkflowIds={initial?.workflowIds}
        onApply={(value, workflowIds) => {
          // Covers-every-type rather than set-equality: a selection can carry
          // ids outside the universe — a type deleted since it was saved — and
          // those extras must not block the collapse to "All".
          const normalized = normalizeAllToEmptyIds(
            workflowIds,
            allWorkflowTypeIds,
          )
          const isDuplicate = items.some(
            (draft, index) =>
              index !== editIndex &&
              areArraysEqualUnordered(draft.workflowIds, normalized),
          )
          if (isDuplicate) {
            toast.error(DUPLICATE_WORKFLOW_SET_ERROR)
            return
          }
          commit({
            restrictionType,
            values: [value],
            workflowIds: normalized,
          })
        }}
        onCancel={close}
      />
    )

  if (selectedCount === 0) return null

  // Every bulk action as a descriptor, so each can render inline (a column
  // button / Delete button) or, when it overflows the row, as a row inside the
  // More menu — from a single definition.
  const bulkActions: IBulkAction[] = [
    {
      key: 'workflowTypes',
      controlKey: WORKFLOW_TYPES_KEY,
      label: Localization.translator.workflowTypes,
      icon: <WorkflowIcon className="tw:size-4" />,
      // p-0/overflow-hidden: edge-to-edge like the Workflow Types cell, so the
      // sort/group bottom sheet covers the full box (the shadcn popover base
      // otherwise adds p-2.5 padding).
      popoverClassName:
        'tw:w-[400px] tw:h-[min(560px,var(--available-height,85vh))] tw:overflow-hidden tw:p-0',
      renderEditor: close => (
        // No recordId: a bulk edit opens with nothing ticked and REPLACES, so what is
        // ticked becomes the value for every selected record - the same contract the
        // permitted-companies action below uses.
        <WorkflowTypeFieldSelector
          resourceTypeId={resourceTypeId}
          className="tw:min-h-0 tw:flex-1"
          // runApply owns the close, and keeps Apply disabled until the write settles -
          // the same in-flight guard the other bulk editors take as `isApplying`.
          isApplyDisabled={isApplying}
          onApply={edits => runApply(() => onBulkWorkflowTypes(edits), close)}
          onClose={close}
        />
      ),
    },
    {
      key: PERMITTED_COMPANIES_ACTION_KEY,
      controlKey: PERMITTED_COMPANIES_KEY,
      label: Localization.translator.permittedCompanies,
      icon: <Building2 className="tw:size-4" />,
      popoverClassName:
        'tw:w-[400px] tw:h-[min(560px,var(--available-height,85vh))] tw:overflow-hidden tw:p-0',
      renderEditor: close => (
        <CompanyListSelector
          selectedCompanyIds={EMPTY_SELECTION}
          companies={companies}
          companyMetadata={companyMetadata}
          defaultGrouping={CompanyGroupingOption.TYPE}
          showActions
          disableApplyWhenEmpty
          onClose={close}
          onApply={ids =>
            runApply(() => onBulkPermittedCompanies(Array.from(ids)), close)
          }
          className="tw:flex-1 tw:min-h-0"
        />
      ),
    },
    {
      key: 'hoursOfOperation',
      controlKey: HOURS_OF_OPERATION_KEY,
      label: Localization.translator.hoursOfOperation,
      icon: <Clock className="tw:size-4" />,
      popoverClassName: 'tw:w-[320px]',
      renderEditor: close => (
        <BulkListEditor<IMsDateInterval>
          label={Localization.translator.hoursOfOperation}
          emptyPlaceholder={Localization.translator.addNew}
          editorPopoverClassName="tw:max-h-[min(560px,calc(100vh-96px))] tw:w-[400px] tw:overflow-y-auto tw:px-0"
          isApplying={isApplying}
          // No initialItems: the selection's rows can hold different schedules, so
          // there is nothing representable to seed — the editor opens on the
          // inherited project-working-hours chip alone.
          allowEmptyApply
          // Same rules as the per-resource cell: one window per weekday, or one
          // every-day window, never both.
          applyItem={upsertOperatingInterval}
          applyItems={(staged, week) => [
            ...week,
            ...staged.filter(
              interval => !resolveOperatingIntervalIsDaily(interval),
            ),
          ]}
          renderChips={({ items, edit, replace }) => {
            const weekRows = buildWeekdayScheduleRows(
              projectDateStore,
              items
                .filter(isWeekdayScopedInterval)
                .map(({ dayOfWeek, startDate, endDate }) => ({
                  dayOfWeek,
                  startDate,
                  endDate: endDate ?? startDate,
                })),
            )

            // A flat array, never a fragment: PopoverTagPicker counts logical
            // children and measures DOM ones, and a fragment is one of the first
            // and several of the second.
            return [
              /* The week is one chip: it opens the whole week, and dismissing it
                 clears the schedule rather than a single day. */
              weekRows.length ? (
                <Tag
                  key="weekly-schedule"
                  className="tw:cursor-pointer"
                  onClick={event => {
                    event.stopPropagation()
                    edit(-1)
                  }}
                  onDismiss={() =>
                    replace(
                      items.filter(
                        interval => !isWeekdayScopedInterval(interval),
                      ),
                    )
                  }
                >
                  <span className="tw:flex tw:flex-col tw:text-left">
                    {weekRows.map(({ label, endsNextDay }) => (
                      <span key={label}>
                        {label}
                        {endsNextDay && <NextDayLabel />}
                      </span>
                    ))}
                  </span>
                </Tag>
              ) : null,
              ...items.map((interval, index) =>
                isWeekdayScopedInterval(interval) ? null : (
                  <Tag
                    key={index}
                    className="tw:cursor-pointer"
                    onClick={event => {
                      event.stopPropagation()
                      edit(index)
                    }}
                    onDismiss={() =>
                      replace(items.filter((_, i) => i !== index))
                    }
                  >
                    {formatOperatingIntervalFmt(projectDateStore, interval)}
                  </Tag>
                ),
              ),
            ]
          }}
          renderLeading={items =>
            findDailyOperatingInterval(items) ? null : (
              <InheritedHoursChip
                label={
                  <span className="tw:flex tw:flex-col tw:text-left">
                    {buildInheritedHoursRows(projectDateStore).map(
                      ({ label, endsNextDay }) => (
                        <span key={label}>
                          {label}
                          {endsNextDay && <NextDayLabel />}
                        </span>
                      ),
                    )}
                  </span>
                }
                onEditProjectHours={onEditProjectHours}
              />
            )
          }
          renderLabel={interval =>
            formatOperatingIntervalFmt(projectDateStore, interval)
          }
          renderEditor={({
            items,
            editIndex,
            commit,
            commitMany,
            close: closeEditor,
          }) => (
            <IntervalEditor
              projectDateStore={projectDateStore}
              initial={buildOperatingIntervalSeed(
                items,
                editIndex,
                projectDateStore,
              )}
              // The same weekly schedule the per-resource cell authors, so a bulk
              // apply can state a week rather than only a single window.
              isWeekdayScheduleEnabled={true}
              initialWeekdays={items
                .filter(isWeekdayScopedInterval)
                .map(interval => {
                  const [startHours, startMinutes] =
                    projectDateStore.getHoursMinutes(interval.startDate)
                  const [endHours, endMinutes] =
                    projectDateStore.getHoursMinutes(
                      interval.endDate ?? interval.startDate,
                    )

                  return {
                    dayOfWeek: interval.dayOfWeek,
                    isWorking: true,
                    startMinutes: startHours * MINUTES_IN_HOUR + startMinutes,
                    endMinutes: endHours * MINUTES_IN_HOUR + endMinutes,
                  }
                })}
              onSaveWeekdays={results =>
                commitMany(
                  results.map(({ startDate, endDate, isDaily, dayOfWeek }) => ({
                    startDate,
                    endDate,
                    isDaily,
                    dayOfWeek,
                  })),
                )
              }
              onSave={result =>
                commit({
                  startDate: result.startDate,
                  endDate: result.endDate,
                  isDaily: result.isDaily,
                  dayOfWeek: result.dayOfWeek,
                })
              }
              onCancel={closeEditor}
            />
          )}
          onApply={items =>
            runApply(() => onBulkOperatingIntervals(items), close)
          }
          onCancel={close}
        />
      ),
    },
    {
      key: 'dateClosures',
      controlKey: DATE_CLOSURES_KEY,
      label: Localization.translator.dateClosures,
      icon: <CalendarOff className="tw:size-4" />,
      popoverClassName: 'tw:w-[320px]',
      renderEditor: close => (
        <BulkListEditor<ILocationClosureInterval>
          label={Localization.translator.dateClosures}
          emptyPlaceholder={Localization.translator.addNew}
          editorPopoverClassName="tw:w-[280px]"
          isApplying={isApplying}
          initialItems={getDefaultClosures()}
          renderLabel={closureInterval =>
            formatClosureIntervalFmt(projectDateStore, closureInterval, false)
          }
          renderEditor={({ initial, commit, close: closeEditor }) => (
            <IntervalEditor
              projectDateStore={projectDateStore}
              initial={initial ?? buildDefaultClosure(projectDateStore)}
              onSave={result =>
                commit({
                  startDate: result.startDate,
                  endDate: result.endDate,
                  isDaily: !!result.isDaily,
                })
              }
              onCancel={closeEditor}
            />
          )}
          onApply={items => runApply(() => onBulkClosures(items), close)}
          onCancel={close}
        />
      ),
    },
    {
      key: MAX_BOOKING_DURATION_ACTION_KEY,
      controlKey: MAX_BOOKING_DURATION_KEY,
      label: Localization.translator.maxBookingDuration,
      icon: <CalendarClock className="tw:size-4" />,
      popoverClassName: 'tw:w-[320px]',
      renderEditor: close => (
        <BulkListEditor<IWorkflowRestrictionDraft>
          label={Localization.translator.maxBookingDuration}
          emptyPlaceholder={Localization.translator.addNew}
          editorPopoverClassName="tw:w-[360px]"
          isApplying={isApplying}
          renderLabel={formatRestrictionDraft}
          renderEditor={renderRestrictionEditor(
            RestrictionType.MaxDuration,
            Localization.translator.maxBookingDuration,
          )}
          onApply={items =>
            runApply(
              () => onBulkRestrictions(RestrictionType.MaxDuration, items),
              close,
            )
          }
          onCancel={close}
        />
      ),
    },
    {
      key: MAX_OVERLAPPING_ACTION_KEY,
      controlKey: MAX_OVERLAPPING_KEY,
      label: Localization.translator.maxOverlapping,
      icon: <Blend className="tw:size-4" />,
      popoverClassName: 'tw:w-[320px]',
      renderEditor: close => (
        <BulkListEditor<IWorkflowRestrictionDraft>
          label={Localization.translator.maxOverlapping}
          emptyPlaceholder={Localization.translator.addNew}
          editorPopoverClassName="tw:w-[360px]"
          isApplying={isApplying}
          renderLabel={formatRestrictionDraft}
          renderEditor={renderRestrictionEditor(
            RestrictionType.MaxOverlapping,
            Localization.translator.maxOverlapping,
          )}
          onApply={items =>
            runApply(
              () => onBulkRestrictions(RestrictionType.MaxOverlapping, items),
              close,
            )
          }
          onCancel={close}
        />
      ),
    },
    {
      key: 'showInMap',
      controlKey: SHOW_IN_MAP_KEY,
      label: Localization.translator.showInMap,
      icon: <MapPin className="tw:size-4" />,
      popoverClassName: 'tw:w-[320px]',
      // Defaults on — a plain display flag (no WR clear/enable cascade); the
      // admin flips it off to bulk-hide the selection from every map.
      renderEditor: close => (
        <BulkToggleEditor
          title={Localization.translator.showInMap}
          defaultChecked
          describe={showInMap =>
            showInMap
              ? Localization.translator.resourceShownInMaps
              : Localization.translator.resourceHiddenFromMaps
          }
          onApply={showInMap =>
            runApply(() => onBulkShowInMap(showInMap), close)
          }
          onCancel={close}
        />
      ),
    },
    {
      key: 'workflowResource',
      label: Localization.translator.workflowResourceBulk,
      icon: <ToggleRight className="tw:size-4" />,
      popoverClassName: 'tw:w-[320px]',
      // Defaults off — the primary bulk path marks rows unavailable and clears
      // their controls; the admin flips it on to bulk-enable.
      renderEditor: close => (
        <BulkToggleEditor
          title={Localization.translator.workflowResourceBulk}
          defaultChecked={false}
          describe={isBookable =>
            isBookable
              ? Localization.translator.resourceAvailableForBooking
              : Localization.translator.resourceNotAvailableForBooking
          }
          onApply={isBookable =>
            runApply(() => onBulkBookable(isBookable), close)
          }
          onCancel={close}
        />
      ),
    },
    {
      key: DELETE_ACTION_KEY,
      label: Localization.translator.delete,
      icon: <Trash2 className="tw:size-4" />,
      onSelect: () => setIsDialogOpen(true),
    },
  ]

  // Show only the actions for controls this resource exposes (structural actions
  // carry no controlKey and always stay). Denial is a separate pass, not a second filter:
  // the site root's actions stay on the row, greyed out.
  const enabledControls = new Set(controls)
  const visibleBulkActions = bulkActions
    .filter(
      action => !action.controlKey || enabledControls.has(action.controlKey),
    )
    .map(action =>
      hasProjectLocationSelected &&
      PROJECT_LOCATION_DENIED_ACTION_KEYS.has(action.key)
        ? { ...action, isDenied: true }
        : action,
    )

  // Inline rendering of each action — measured by DynamicOverflowList and shown
  // on the row until it runs out of width. A button only sets the active key
  // (capturing its own element as the editor anchor) or runs a direct onSelect;
  // the editor itself lives at the bar level.
  const inlineItems = visibleBulkActions.map(action => (
    // The key stays on the OUTERMOST element: DynamicOverflowList keys its measuring
    // wrapper by item.key, so moving the wrapper without it would remount on every reflow.
    <DisabledReasonTooltip
      key={action.key}
      isDenied={!!action.isDenied}
      reason={action.isDenied ? deniedReasonFor(action) : ''}
    >
      <Button
        variant={action.onSelect ? 'destructive-ghost' : 'primary-ghost'}
        // Reflect the open editor as primary-ghost's "expanded" state so the
        // active action gets the design's primary-soft highlight.
        aria-expanded={activeActionKey === action.key}
        // Greyed while a bulk write is in flight: activateAction refuses to switch then,
        // and a button that silently does nothing is worse than one that says so.
        disabled={action.isDenied || isApplying}
        onClick={event =>
          action.onSelect
            ? action.onSelect()
            : activateAction(action.key, event.currentTarget, 'start')
        }
      >
        {action.icon}
        {action.label}
      </Button>
    </DisabledReasonTooltip>
  ))

  const activeAction = visibleBulkActions.find(
    action => action.key === activeActionKey,
  )
  // Anchor as a value (not a function) so base-ui repositions when it changes —
  // e.g. clicking another action while an editor is open moves the popover to
  // the new trigger. The live element while it's mounted; a stable frozen-rect
  // anchor once it unmounts (its action reflowed away), so the popover holds its
  // place instead of jumping.
  const anchor = activeActionKey ? activeAnchorRef.current : null
  let editorAnchor:
    | HTMLElement
    | { getBoundingClientRect: () => DOMRect }
    | null
  if (!anchor) {
    editorAnchor = null
  } else if (anchor.el.isConnected) {
    editorAnchor = anchor.el
    frozenAnchorRef.current = null
  } else {
    frozenAnchorRef.current ??= { getBoundingClientRect: () => anchor.rect }
    editorAnchor = frozenAnchorRef.current
  }

  return (
    <>
      <DynamicOverflowList
        items={inlineItems}
        store={overflowStore}
        shouldUseItemKey
        shouldIgnoreScrollBarWidth
        moreButtonWidth={MORE_BUTTON_WIDTH}
        paddingWidth={ITEM_GAP_BUFFER}
        elementsClassName="tw:mr-1 tw:align-middle"
      >
        {({ visibleElements, overflowElements, containerRefSetter }) => {
          // overflowElements preserves order, so the actions that didn't fit are
          // the tail of the descriptor list past the visible count.
          const overflowActions = visibleBulkActions.slice(
            visibleElements.length,
          )
          return (
            // Inline-flow (not flex): DynamicOverflowList wraps each item in an
            // inline-block and measures its natural width. A flex container would
            // shrink those wrappers, so every item would mis-measure as "fits"
            // and overflow silently instead of collapsing into More.
            <div
              ref={containerRefSetter}
              className="tw:w-full tw:overflow-hidden tw:whitespace-nowrap"
            >
              {visibleElements}
              {overflowElements.length > 0 && (
                <span className="tw:inline-block tw:align-middle">
                  <BulkMoreMenu
                    menuActions={overflowActions}
                    onActivate={key =>
                      activateAction(key, moreTriggerRef.current, 'end')
                    }
                    isApplying={isApplying}
                    registerAnchor={el => {
                      moreTriggerRef.current = el
                    }}
                  />
                </span>
              )}
            </div>
          )
        }}
      </DynamicOverflowList>

      {/* Single bar-level editor for the active action. Anchored to a snapshot
          of the opening trigger (live rect while it exists, frozen rect once it
          unmounts), so it survives the action reflowing across the
          inline/overflow boundary and stays where it was opened. */}
      <Popover
        open={!!activeAction}
        onOpenChange={open => {
          if (!open) closeEditor()
        }}
      >
        <PopoverContent
          anchor={editorAnchor}
          align={activeAnchorRef.current?.align ?? 'end'}
          className={activeAction?.popoverClassName}
          finalFocus={() => activeAnchorRef.current?.el ?? null}
        >
          {/* Key by the active action so the editor's mount-only state — including
              the seeded staged list — is fresh on every open and on direct
              switches between actions, rather than reused from the prior editor. */}
          <React.Fragment key={activeActionKey}>
            {activeAction?.renderEditor?.(closeEditor)}
          </React.Fragment>
        </PopoverContent>
      </Popover>

      <ConfirmDialog
        useRedConfirmationButton
        title={Localization.translator.deleteSelected}
        isOpen={isDialogOpen}
        loading={isDeleting}
        onCancelClicked={isDeleting ? NOOP : () => setIsDialogOpen(false)}
        onDoneClicked={handleDelete}
        doneTitle={Localization.translator.delete}
      >
        <div className="tw:pb-2.5 tw:text-body-md tw:whitespace-pre-line">
          {confirmDelete(selectedCount)}
        </div>
      </ConfirmDialog>
    </>
  )
}

export default observer(ResourceControlsSelectionBar)
